import { Flex } from '@chakra-ui/react';
import { CANVAS_ZOOM } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store';
import {
  selectEditBackgroundView,
  selectIsAccessibilityView,
  selectIsSlideView,
  selectIsWidgetSettingsView,
  selectZoom,
} from 'modules/Editor/store/editorSettingsSelector';
import { setZoom } from 'modules/Editor/store/editorSettingsSlice';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { Keyboard } from 'modules/common/components/Keyboard';
import { NumberInputPopover } from 'modules/common/components/ToolbarPopover';
import { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { debounce } from 'utils/debounce';

import { useBoundingBox } from '../BoundingBox/useBoundingBox';
import { useFocus } from '../Focus';
import {
  DEFAULT_CENTER_RATIO,
  PRESET_ZOOM_LEVELS,
  ZOOM_CHANGE_STEP,
  ZOOM_CONTROL_INITIAL_OFFSET,
  ZOOM_CONTROL_INPUT_TEST_ID,
  ZOOM_CONTROL_LABEL,
  ZOOM_CONTROL_OFFSET_WITH_SIDE_PANEL,
  ZOOM_CONTROL_TEST_ID,
  ZOOM_IN_BTN_TOOLTIP,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_OUT_BTN_TOOLTIP,
  ZOOM_SCROLL_STEP,
} from './ZoomControl.config';
import {
  adjustPageZoom,
  getCurrentScrollRatios,
  scrollToFocusPosition,
  unsetPageSizeProperty,
} from './ZoomControl.helpers';

export const ZoomControl = memo(() => {
  const [rightOffset, setRightOffset] = useState(`${ZOOM_CONTROL_INITIAL_OFFSET}px`);
  const { pageScrollContainerRef } = useFocus();

  const isSlideView = useAppSelector(selectIsSlideView);
  const isAccessibilityView = useAppSelector(selectIsAccessibilityView);
  const isWidgetSettingsView = useAppSelector(selectIsWidgetSettingsView);
  const isBackgroundMenuOpen = useAppSelector(selectEditBackgroundView);

  // Adjust the zoom control position if any of the right menus are opened
  useLayoutEffect(() => {
    const pageScrollEl = pageScrollContainerRef?.current;
    if (!pageScrollEl) return;

    let offset = ZOOM_CONTROL_INITIAL_OFFSET;
    if (isSlideView || isAccessibilityView || isWidgetSettingsView || isBackgroundMenuOpen) {
      offset = ZOOM_CONTROL_OFFSET_WITH_SIDE_PANEL;
      const hasVerticalScrollbar = pageScrollEl.scrollHeight > pageScrollEl.clientHeight;
      const extraOffset = hasVerticalScrollbar ? 16 : 0;
      offset += extraOffset;
    }

    setRightOffset(`${offset}px`);
  }, [pageScrollContainerRef, isAccessibilityView, isSlideView, isWidgetSettingsView, isBackgroundMenuOpen]);

  return (
    <Flex
      w='fit-content'
      position='absolute'
      right={rightOffset}
      bottom='6'
      zIndex='pageManager'
      data-testid={ZOOM_CONTROL_TEST_ID}
    >
      <ZoomWheel />
      <ZoomSelect />
    </Flex>
  );
});
ZoomControl.displayName = 'ZoomControl';

// Handles the pinch and scroll zooming
const ZoomWheel = memo(() => {
  const dispatch = useAppDispatch();
  const activePageId = useAppSelector(selectActivePage);
  const zoom = useAppSelector(selectZoom);

  const zoomRef = useRef<number>(zoom);
  zoomRef.current = zoom;

  // isScrollRef is used as flag to detect whether it's a pure scroll event to prevent pressing cmd or ctrl in the middle of scroll from triggering zoom
  const isScrollRef = useRef<boolean>(false);

  // isScrollRef is used as flag to detect whether it's scroll event for zooming. When using cmd/ctrl + finger gesture to zoom on Mac, it takes ~2 seconds for event.deltaY to slowly drop to 0, when this flag is still on (the flag is kept on for another 0.2 seconds after cmd or ctrl is released), disable the pure scroll from being triggered
  const isZoomScrollRef = useRef<boolean>(false);

  const { boundingBox } = useBoundingBox();
  const { pageScrollContainerRef, pageSizeAreaRef, pageScaleAreaRef, pageFocusWrapperRef } = useFocus();

  const debouncedOnWheelEnd = debounce(() => {
    dispatch(setZoom(zoomRef.current));
    isScrollRef.current = false;
    isZoomScrollRef.current = false;
    unsetPageSizeProperty(pageSizeAreaRef);
  }, 200);

  const debouncedOnPureScrollEnd = debounce(() => {
    isScrollRef.current = false;
  }, 200);

  // pass mouse position relative to pageScrollContainer, calculate the focus point dimension ratios with regard to pageFocusWrapper
  const getMousePositionRatios = useCallback(
    (mouseRelativePos: [number, number]): [number, number] => {
      const pageScrollEl = pageScrollContainerRef?.current;
      const pageFocusWrapperEl = pageFocusWrapperRef?.current;

      let xRatio = DEFAULT_CENTER_RATIO;
      let yRatio = DEFAULT_CENTER_RATIO;

      if (pageScrollEl && pageFocusWrapperEl) {
        const { height: pageFocusWrapperH, width: pageFocusWrapperW } = pageFocusWrapperEl.getBoundingClientRect();
        xRatio = (pageScrollEl.scrollLeft + mouseRelativePos[0]) / pageFocusWrapperW;
        yRatio = (pageScrollEl.scrollTop + mouseRelativePos[1]) / pageFocusWrapperH;
      }

      return [xRatio, yRatio];
    },
    [pageScrollContainerRef, pageFocusWrapperRef],
  );

  const scrollToMousePosition = useCallback(
    (scrollRatios: [number, number], mouseRelativePos: [number, number]) => {
      const pageScrollEl = pageScrollContainerRef?.current;
      const pageFocusWrapperEl = pageFocusWrapperRef?.current;

      if (pageScrollEl && pageFocusWrapperEl) {
        const { height: pageFocusWrapperH, width: pageFocusWrapperW } = pageFocusWrapperEl.getBoundingClientRect();
        pageScrollEl.scrollLeft = scrollRatios[0] * pageFocusWrapperW - mouseRelativePos[0];
        pageScrollEl.scrollTop = scrollRatios[1] * pageFocusWrapperH - mouseRelativePos[1];
      }
    },
    [pageScrollContainerRef, pageFocusWrapperRef],
  );

  const onWheel = useCallback(
    (event: WheelEvent) => {
      if (!event.metaKey && !event.ctrlKey) {
        if (isZoomScrollRef.current) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        isScrollRef.current = true;
        debouncedOnPureScrollEnd();
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const pageScrollEl = pageScrollContainerRef?.current;
      const sizeEl = pageSizeAreaRef?.current;
      const scaleEl = pageScaleAreaRef?.current;

      if (!isScrollRef.current && event.deltaY !== 0 && sizeEl && scaleEl && pageScrollEl) {
        isZoomScrollRef.current = true;
        let deltaYMagnifier = 1;

        // For pinch zoom on Mac, adjust the deltaYMagnifier calculated by a continuous function
        // (the function is created with trial and error approach)
        if (navigator.userAgent.indexOf('Mac') !== -1 && event.ctrlKey) {
          deltaYMagnifier = Math.min(3 * Math.abs(event.deltaY) ** 0.2, 4);
        }

        const stepsNum = Math.floor(Math.abs(event.deltaY * deltaYMagnifier) / 2);
        let tempZoom =
          event.deltaY > 0
            ? zoomRef.current - ZOOM_SCROLL_STEP * stepsNum
            : zoomRef.current + ZOOM_SCROLL_STEP * stepsNum;
        tempZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, tempZoom));

        const { x: pageScrollX, y: pageScrollY } = pageScrollEl.getBoundingClientRect();
        const mouseRelativePos = [event.clientX - pageScrollX, event.clientY - pageScrollY] as [number, number];
        const currentScrollRatios = getMousePositionRatios(mouseRelativePos);

        adjustPageZoom(tempZoom, sizeEl, scaleEl);
        if (sizeEl && scaleEl) zoomRef.current = tempZoom;

        boundingBox.updateRect();
        scrollToMousePosition(currentScrollRatios, mouseRelativePos);
        debouncedOnWheelEnd();
      }
    },
    [
      pageScrollContainerRef,
      pageSizeAreaRef,
      pageScaleAreaRef,
      debouncedOnPureScrollEnd,
      getMousePositionRatios,
      boundingBox,
      scrollToMousePosition,
      debouncedOnWheelEnd,
    ],
  );

  // add the wheel event listener to pageScrollContainer
  useLayoutEffect(() => {
    const pageScrollEl = pageScrollContainerRef?.current;
    pageScrollEl?.addEventListener('wheel', onWheel, { passive: false });

    return () => pageScrollEl?.removeEventListener('wheel', onWheel);
  }, [onWheel, pageScrollContainerRef, activePageId]);

  return <> </>;
});

ZoomWheel.displayName = 'ZoomWheel';

const zoomSelectPopoverStyle = {
  backgroundColor: 'white',
  borderRadius: 'var(--vg-radii-base)',
  boxShadow: 'var(--vg-shadows-sm)',
  border: 'none',
};
const zoomSelectBtnStyle = { width: 8, noBorder: true };

const ZoomSelect = memo(() => {
  const zoom = useAppSelector(selectZoom);
  const dispatch = useAppDispatch();

  const { pageScrollContainerRef, pageSizeAreaRef, pageScaleAreaRef, pageFocusWrapperRef } = useFocus();

  const handleZoomIncrement = () => {
    const currentScrollRatios = getCurrentScrollRatios(pageScrollContainerRef, pageFocusWrapperRef);
    const tempZoom = Math.min(zoom + ZOOM_CHANGE_STEP, ZOOM_MAX);

    adjustPageZoom(tempZoom, pageSizeAreaRef?.current, pageScaleAreaRef?.current);
    scrollToFocusPosition(pageScrollContainerRef, pageFocusWrapperRef, currentScrollRatios);
    dispatch(setZoom(tempZoom));
    unsetPageSizeProperty(pageSizeAreaRef);

    Mixpanel.track(CANVAS_ZOOM.eventName, {
      from: CANVAS_ZOOM.from.ZoomButton,
      zoom_level: Math.round(tempZoom * 100),
    });
  };

  const handleZoomDecrement = () => {
    const currentScrollRatios = getCurrentScrollRatios(pageScrollContainerRef, pageFocusWrapperRef);
    const tempZoom = Math.max(zoom - ZOOM_CHANGE_STEP, ZOOM_MIN);

    adjustPageZoom(tempZoom, pageSizeAreaRef?.current, pageScaleAreaRef?.current);
    scrollToFocusPosition(pageScrollContainerRef, pageFocusWrapperRef, currentScrollRatios);
    dispatch(setZoom(tempZoom));
    unsetPageSizeProperty(pageSizeAreaRef);

    Mixpanel.track(CANVAS_ZOOM.eventName, {
      from: CANVAS_ZOOM.from.ZoomButton,
      zoom_level: Math.round(tempZoom * 100),
    });
  };

  const handleChangeZoomValue = (value: number) => {
    const currentScrollRatios = getCurrentScrollRatios(pageScrollContainerRef, pageFocusWrapperRef);
    const tempZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, value / 100));

    adjustPageZoom(tempZoom, pageSizeAreaRef?.current, pageScaleAreaRef?.current);
    scrollToFocusPosition(pageScrollContainerRef, pageFocusWrapperRef, currentScrollRatios);
    dispatch(setZoom(tempZoom));
    unsetPageSizeProperty(pageSizeAreaRef);

    Mixpanel.track(CANVAS_ZOOM.eventName, {
      from: CANVAS_ZOOM.from.ZoomInput,
      zoom_level: Math.round(tempZoom * 100),
    });
  };

  return (
    <NumberInputPopover
      dropdownOptions={PRESET_ZOOM_LEVELS}
      inputTestId={ZOOM_CONTROL_INPUT_TEST_ID}
      label={ZOOM_CONTROL_LABEL}
      onChange={handleChangeZoomValue}
      onIncrement={handleZoomIncrement}
      onDecrement={handleZoomDecrement}
      value={Math.round(zoom * 100)}
      suffix='%'
      incrementLabel={ZOOM_IN_BTN_TOOLTIP}
      incrementShortCutLabel={
        <>
          {ZOOM_IN_BTN_TOOLTIP} <Keyboard shortcut={['ctrl', 'alt', '+']} macOs={['ctrl', '+']} />
        </>
      }
      decrementLabel={ZOOM_OUT_BTN_TOOLTIP}
      decrementShortCutLabel={
        <>
          {ZOOM_OUT_BTN_TOOLTIP} <Keyboard shortcut={['ctrl', 'alt', '-']} macOs={['ctrl', '-']} />
        </>
      }
      min={ZOOM_MIN * 100}
      max={ZOOM_MAX * 100}
      popOverStyleProps={zoomSelectPopoverStyle}
      stepperBtnStyles={zoomSelectBtnStyle}
    />
  );
});
ZoomSelect.displayName = 'ZoomSelect';
